/**
 * Welle 14 Polish — Globale Keyboard-Shortcuts.
 *
 * Pattern: vim-artige Chord-Shortcuts (`g d` = Go Dashboard) + Standalone-Tasten.
 * - Aktiv NUR wenn focus NICHT in Input/Textarea/Select/contenteditable steht.
 * - Aktiv NUR wenn keine Modifier-Keys (Cmd/Ctrl/Alt) gedrückt sind — damit
 *   normale Browser-Shortcuts (Cmd-K, Cmd-T, etc.) unverändert funktionieren.
 *
 * Wird einmal in App.tsx registriert.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface Shortcut {
  /** Chord-Schlüssel: 'g d' für "g then d", 'd' für Einzeltaste. */
  chord: string;
  description: string;
  /** Aktion ausführen; bekommt navigate, optional setHelpOpen. */
  run: (ctx: { navigate: ReturnType<typeof useNavigate>; setHelpOpen: (v: boolean) => void }) => void;
}

export const SHORTCUTS: Shortcut[] = [
  // Navigation
  { chord: 'g d', description: 'Dashboard', run: ({ navigate }) => navigate('/') },
  { chord: 'g w', description: 'Watchlist', run: ({ navigate }) => navigate('/watchlist') },
  { chord: 'g o', description: 'Positionen', run: ({ navigate }) => navigate('/positions') },
  { chord: 'g s', description: 'Wie entschieden wird', run: ({ navigate }) => navigate('/strategies') },
  { chord: 'g j', description: 'Journal', run: ({ navigate }) => navigate('/journal') },
  { chord: 'g e', description: 'Einstellungen', run: ({ navigate }) => navigate('/settings') },
  { chord: 'g a', description: 'Admin Cron-Health', run: ({ navigate }) => navigate('/admin/cron-health') },
  // Single-Key
  { chord: '?', description: 'Diese Hilfe öffnen', run: ({ setHelpOpen }) => setHelpOpen(true) },
];

function isEditingContext(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Liefert einen Trigger zum Öffnen des Help-Modals.
 * Nutzung: in App.tsx als Effect aufrufen, das Help-Modal-State-Pair übergeben.
 */
export function useKeyboardShortcuts(setHelpOpen: (v: boolean) => void): void {
  const navigate = useNavigate();
  const pendingPrefixRef = useRef<string | null>(null);
  const prefixTimerRef = useRef<number | null>(null);

  useEffect(() => {
    function clearPrefix() {
      pendingPrefixRef.current = null;
      if (prefixTimerRef.current !== null) {
        clearTimeout(prefixTimerRef.current);
        prefixTimerRef.current = null;
      }
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditingContext(e.target)) return;

      // ESC bricht Chord-Wait ab
      if (e.key === 'Escape') {
        clearPrefix();
        return;
      }

      const key = e.key.toLowerCase();
      if (key.length !== 1 && key !== '?') return;

      // Wenn schon Prefix wartet: probiere Chord 'PREFIX key'
      if (pendingPrefixRef.current) {
        const chord = `${pendingPrefixRef.current} ${key}`;
        const sc = SHORTCUTS.find((s) => s.chord === chord);
        clearPrefix();
        if (sc) {
          e.preventDefault();
          sc.run({ navigate, setHelpOpen });
        }
        return;
      }

      // Erstmal: prüfe Single-Key-Shortcut
      const single = SHORTCUTS.find((s) => s.chord === key);
      if (single) {
        e.preventDefault();
        single.run({ navigate, setHelpOpen });
        return;
      }

      // Sonst: ist 'key' ein Prefix für irgendeinen Chord?
      const hasChord = SHORTCUTS.some((s) => s.chord.startsWith(`${key} `));
      if (hasChord) {
        pendingPrefixRef.current = key;
        // 1.5 s warten, dann verwerfen
        prefixTimerRef.current = window.setTimeout(clearPrefix, 1500);
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      clearPrefix();
    };
  }, [navigate, setHelpOpen]);
}
